import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { TSelectOption } from '@mfe-types/field';

interface Mapping {
    atlas: MappingItem[] | null;
    places: MappingItem[] | null;
    immoweb: MappingItem[] | null;
    immowelt: MappingItem[] | null;
    logicimmo: MappingItem[] | null;
    meilleursagents: MappingItem[] | null;
}

interface Name {
    name: string;
    slug: string | null;
    official: boolean;
    name_rank: number;
    name_root: string | null;
    name_prefix: string | null;
    display_name: string;
}

type Names = Record<string, Name[]>;

interface Metric {
    testname: string;
    confidence: number;
}

interface MappingItem {
    id: number | string;
    type: string;
    metric: Metric;
    platform?: string;
}

interface Coordinates {
    lat: number;
    lng: number;
}

interface Metadata {
    search_time: number;
    query_duration: number;
    next_page_keyset: any;
    count: number;
    level_of_detail: any;
}

export enum ParentTypes {
    Street = 'Street',
    Municipality = 'Municipality',
    Borough = 'Borough',
}

export enum PlaceTypes {
    HouseNumber = 'HONU',
    Street = 'STRT',
    Municipality = 'AD08',
    Borough = 'AD09',
}

export enum GeoCountry {
    France = 'FR',
    Germany = 'DE',
    Belgium = 'BE',
}

export enum AddressLevel {
    City = 'city',
    Street = 'street',
    HouseNumber = 'houseNumber',
}

export const PLACE_TYPES_LIST = ['HONU', 'STRT', 'AD09', 'AD08'] as const;

export type TPlaceTypes = `${PlaceTypes}`;
export type TGeoCountry = `${GeoCountry}`;
export type TAddressLevel = `${AddressLevel}`;

export interface AutocompleteAPIResponse {
    metadata: Metadata;
    items: GeoPlace[];
}

export interface PlaceAPIErrorResponse {
    detail: Array<{
        ctx: { pattern: string };
        input: string;
        loc: Array<string>;
        msg: string;
        type: string;
        url: string;
    }>;
}

export interface PlaceAPIResponse {
    metadata: Metadata;
    item: GeoPlace;
}

export interface GeoMatchPlace {
    id: string;
    type_key: TPlaceTypes;
    coordinates?: Coordinates;
    match_name: string;
    parents: GeoMatchPlace[];
}

export interface GeoMatchItem {
    match: GeoMatchPlace;
}
export interface GeocodingAPIResponse {
    items: {
        aviv?: GeoMatchItem[];
    };
}

export interface GeoPlace {
    id: string;
    area: number;
    type: string;
    level: number;
    names: Names;
    active: boolean;
    mapping: Mapping;
    language: string;
    type_key: PlaceTypes;
    coordinates: Coordinates;
    postal_codes: null | string[];
    main_postal_code: string;
    parents: GeoPlace[] | null;
}

export interface City extends TSelectOption {
    id: string | null;
    postalCode: string | null;
}

export enum LocationError {
    CityNameNotFound = 'CityNameNotFound',
    CityNameException = 'CityNameException',
}

export interface AutocompleteData {
    id: string;
    name: string;
    displayName: string;
    postalCode: string;
}

export interface AutocompleteRequestPayload {
    text: string;
    portal: Portal;
    addressLevel: TAddressLevel;
    limit?: number;
    parentId?: string;
}

export interface GermanyAutocompleteRequestPayload extends AutocompleteRequestPayload {
    isGermanPortal: boolean;
    locale: TLocale;
}

export interface AutocompleteParams {
    text: string;
    countries?: Array<TGeoCountry>;
    place_types?: Array<TPlaceTypes>;
    limit?: number;
    parent_types?: Array<TPlaceTypes>;
    parent_ids?: Array<string>;
}

export interface GeocodingParams {
    country: 'FR' | 'BE' | 'DE' | 'AT' | 'CH' | undefined;
    providers?: Array<'google' | 'mapbox' | 'aviv'>;
    number: string;
    address: string;
    city: string;
    postal_code: string;
    add_debug_events?: boolean;
}

export type LegacyType = 'addresses' | 'streets' | 'cities';

export interface LegacyPlaceParams {
    id: string | null;
    type: LegacyType | null;
}

export interface PlaceParams {
    postalCode: string | null;
    cityName: string | null;
    streetName: string | null;
    houseNumber: string | null;
    geoId: string | null;
}

export interface PlaceParamsWithAtLeastPostalCode extends PlaceParams {
    postalCode: string;
    isFrenchPortal: boolean;
}
